import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
} from 'rxjs/operators';
import { OperatorFunction, Observable, Subject, merge, Subscription } from 'rxjs';
import {
  NgbActiveModal,
  NgbModal,
  NgbTypeahead,
  NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { FormGroup } from '@angular/forms';
import { BankModel } from 'src/app/models/bank.model';
import { BankService } from 'src/app/services/api/bank.service';
import { BalanceService } from 'src/app/services/api/balance.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/api/user.service';
import { PinModel } from 'src/app/models/pin.model';
import { ResponseCustomType } from 'src/app/types/response/response.type';
import { BalanceResponseType } from 'src/app/types/response/balance-response.type';
import { navigation } from '../../../../constants/navigation.const';
import { Router } from '@angular/router';
import { BypassApiService } from 'src/app/services/api/bypass.service';
import { environment } from 'src/environments/environment';
import { ProfileService } from 'src/app/services/profile.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { OTPModel } from 'src/app/models/otp-input.model';
import { BalanceRequestType } from 'src/app/types/request/balance-request.type';
import { UpdatePinTypes } from 'src/app/types/request/update-pin-types';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  standalone: false,
  selector: 'app-modal-tarik-saldo',
  templateUrl: './modal-tarik-saldo.component.html',
  styleUrls: ['./modal-tarik-saldo.component.scss'],
})
export class ModalTarikSaldoComponent implements OnInit, OnDestroy {
  states: BankModel[] = [];
  bank!: BankModel;
  @Input() pin!: PinModel;
  accountNumber!: number;
  destroy$ = new Subject<void>();
  formGroup!: FormGroup;
  nominal!: number;
  textAlertMin: string = '';
  isDisabled: boolean = true;
  isErrorMessage: boolean = false;
  otp: string = '000000';
  settings: OTPModel = {
    length: 6,
    numbersOnly: true,
  };
  @Output() filterResult = new EventEmitter<BalanceRequestType>();
  @Input() paramsWD: BalanceRequestType = {
    bank_account_id: '',
    nominal: '',
    type: 'balance',
    pin_token: '',
  };
  @Input() otpParams: UpdatePinTypes = {
    pin: '',
  };
  @ViewChild('instance', { static: true })
  instance!: NgbTypeahead;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();
  balance: BalanceResponseType = {
    balance: 0,
    commission: 0,
  };
  navigation = navigation;
  isEnterprise: boolean = false;
  private profileSubscription!: Subscription;
  withdrawalToken: string = "";

  constructor(
    private activeModal: NgbActiveModal,
    private bankService: BankService,
    private modalService: NgbModal,
    private userService: UserService,
    private toastService: ToastService,
    private balanceService: BalanceService,
    private route: Router,
    private byPassService: BypassApiService,
    private profileService: ProfileService,
    private message: NzMessageService,
  ) { }

  ngOnInit(): void {
    this.getProfile();
  }

  getProfile(): void {
    this.profileSubscription = this.profileService.profileData$.subscribe((data) => {
      if (data) {
        this.isEnterprise = data.rajaongkir_level === 3;
        this.getBalance();
        this.getBankList();
      }
    });
  }

  getBalance(): void {
    if (this.isEnterprise) {
      this.balanceService
        .balance()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ResponseCustomType<BalanceResponseType>) => {
            this.balance = res.data;
          },
          error: (err: HttpErrorResponse) => {
            this.message.create('error', err.error?.data?.errors);
          }
        });
    } else {
      this.byPassService
        .request<ResponseCustomType<BalanceResponseType>>({
          method: 'GET',
          endpoint: `${environment.SWITCH_URL}/user/api/v1/user/balance`
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ResponseCustomType<BalanceResponseType>) => {
            this.balance = res.data;
          },
          error: (err: HttpErrorResponse) => {
            this.message.create('error', err.error?.data?.errors);
          }
        });
    }
  }

  getBankList(): void {
    if (this.isEnterprise) {
      this.bankService
        .bankList()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ResponseCustomType<BankModel[]>) => {
            this.states = res.data;
            this.bank = res.data[0];
            this.paramsWD.bank_account_id = res.data[0].id;
            this.filterResult.emit(this.paramsWD);
          },
          error: (err: HttpErrorResponse) => {
            this.message.create('error', err.error?.data?.errors);
          }
        });
    } else {
      this.byPassService
        .request<ResponseCustomType<BankModel[]>>({
          method: 'GET',
          endpoint: `${environment.SWITCH_URL}/user/api/v1/bank-account/list`
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ResponseCustomType<BankModel[]>) => {
            this.states = res.data;
            this.bank = res.data[0];
            this.paramsWD.bank_account_id = res.data[0].id;
            this.filterResult.emit(this.paramsWD);
          },
          error: (err: HttpErrorResponse) => {
            this.message.create('error', err.error?.data?.errors);
          }
        });
    }
  }

  inputBank(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedId = target.value;

    this.paramsWD.bank_account_id = parseInt(selectedId, 10);
    this.filterResult.emit(this.paramsWD);
  }

  selectedItem(e: NgbTypeaheadSelectItemEvent<BankModel>): void {
    this.bank = e.item;
  }

  formatter = (x: {
    bank_name: string;
    bank_code: string;
    bank_owner: string;
    bank_number: number;
  }) => x.bank_code + ' - ' + x.bank_number + ' - ' + x.bank_owner;

  search: OperatorFunction<string, readonly { bank_name: string }[]> = (
    text$: Observable<string>
  ) => {
    const debouncedText$ = text$.pipe(
      debounceTime(200),
      distinctUntilChanged()
    );
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.instance.isPopupOpen())
    );
    const inputFocus$ = this.focus$;

    return merge(
      debouncedText$,
      inputFocus$,
      clicksWithClosedPopup$,
      text$
    ).pipe(
      debounceTime(500),
      map((term) =>
        term === ''
          ? []
          : this.states
            .filter(
              (v: BankModel) =>
                v.bank_name.toLowerCase().indexOf(term.toLowerCase()) > -1
            )
            .slice(0, 10)
      )
    );
  };

  handleClose(_e?: MouseEvent | Event): void {
    this.activeModal.dismiss();
    this.textAlertMin = '';
  }

  handleConfirm(content: TemplateRef<unknown>): void {
    this.activeModal.dismiss();
    this.textAlertMin = '';
    this.modalService.open(content, {
      backdrop: 'static',
      centered: true,
    });
  }

  close(content?: TemplateRef<unknown>): void {
    this.modalService.dismissAll(content);
    this.modalService.open(ModalTarikSaldoComponent, {
      backdrop: 'static',
      centered: true,
    });
    this.isErrorMessage = false;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Backspace') {
      this.isErrorMessage = false;
      this.isDisabled = true;
    }
  }

  onChange(e: string): void {
    this.otp = e;
    if (e.length === 6) {
      this.isDisabled = false;
    }
  }

  handleVerif(content?: TemplateRef<unknown>): void {
    this.otpParams.pin = this.otp;
    if (this.isEnterprise) {
      this.userService
        .checkPin(this.otpParams)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ResponseCustomType<PinModel>) => {
            if (res.data.is_match === true) {
              this.pin = res.data;
              this.paramsWD.pin_token = res.data.pin_token;
              this.handleWD(content);
            } else {
              this.isErrorMessage = true;
            }
          },
          error: (err: HttpErrorResponse) => {
            this.message.create('error', err.error?.meta?.message);
          }
        });
    } else {
      this.byPassService
        .request<ResponseCustomType<PinModel>>({
          method: 'GET',
          endpoint: `${environment.SWITCH_URL}/user/api/v1/user/check-pin`,
          params: this.otpParams
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ResponseCustomType<PinModel>) => {
            if (res.data.is_match === true) {
              this.pin = res.data;
              this.paramsWD.pin_token = res.data.pin_token;
              this.handleWD(content);
            } else {
              this.isErrorMessage = true;
            }
          },
          error: (err: HttpErrorResponse) => {
            this.message.create('error', err.error?.meta?.message);
          }
        });
    }
  }

  handleWD(content?: TemplateRef<unknown>): void {
    if (this.isEnterprise) {
      this.balanceService
        .request(this.paramsWD)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ResponseCustomType<unknown>) => {
            if (res.meta.code === 201) {
              this.toastService.show(
                null,
                'Successfully submitted commission withdrawal request',
                'success',
                {
                  classname: 'bg-success',
                  delay: 5000,
                }
              );
              this.modalService.dismissAll(content);
              const currentRoute = this.route.url;
              if (currentRoute.endsWith('/finance/balance')) {
                window.location.reload();
              }
            }
          },
          error: (err: HttpErrorResponse) => {
            this.message.create('error', err.error?.meta?.message);
          }
        });
    } else {
      this.byPassService
        .request<ResponseCustomType<unknown>>({
          method: 'POST',
          endpoint: `${environment.SWITCH_URL}/user/api/v1/withdrawal/request`,
          body: this.paramsWD
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ResponseCustomType<unknown>) => {
            if (res.meta.code === 201) {
              this.toastService.show(
                null,
                'Successfully submitted commission withdrawal request',
                'success',
                {
                  classname: 'bg-success',
                  delay: 5000,
                }
              );
              this.modalService.dismissAll(content);
              const currentRoute = this.route.url;
              if (currentRoute.endsWith('/finance/balance')) {
                window.location.reload();
              }
            }
          },
          error: (err: HttpErrorResponse) => {
            this.message.create('error', err.error?.meta?.message);
          }
        });
    }
  }

  handleNominal(value: string | number): void {
    if (value === '') {
      this.isDisabled = true;
      this.textAlertMin = '';
    } else {
      this.isDisabled = false;
      this.textAlertMin = '';
    }

    const input = document.getElementById('nominal-input') as HTMLInputElement | null;
    if (!input) {
      return;
    }
    let inputVal = input.value;

    if (inputVal === '' || inputVal === 'Rp' || inputVal === 'Rp ') {
      return;
    }

    const originalLen = inputVal.length;
    let caretPos = input.selectionStart ?? 0;

    if (inputVal.indexOf(',') >= 0) {
      const decimalPos = inputVal.indexOf(',');
      let leftSide = inputVal.substring(0, decimalPos);
      let rightSide = inputVal.substring(decimalPos);

      leftSide = this.formatNumber(leftSide);
      rightSide = this.formatNumber(rightSide);

      rightSide = rightSide.substring(0, 2);
      inputVal = leftSide + ',' + rightSide; // eslint-disable-line
    } else {
      inputVal = inputVal.replace(/\./g, '');
      inputVal = this.formatNumber(inputVal);
    }

    input.value = inputVal;

    const updatedLen = inputVal.length;
    caretPos = updatedLen - originalLen + caretPos;
    input.setSelectionRange(caretPos, caretPos);

    const valEnd = inputVal.replace(/\./g, '');

    if (parseInt(valEnd, 10) > 30000000) {
      this.isDisabled = true;
      this.textAlertMin = 'Maximum withdrawal request Rp 30,000,000';
    } else if (parseInt(valEnd, 10) > this.balance.balance) {
      this.isDisabled = true;
      this.textAlertMin = 'Insufficient balance';
    } else if (parseInt(valEnd, 10) < 10000) {
      this.isDisabled = true;
      this.textAlertMin = 'Minimum withdrawal request Rp 10,000';
    } else {
      this.isDisabled = false;
      this.textAlertMin = '';
    }

    this.paramsWD.nominal = parseInt(valEnd, 10);
    this.filterResult.emit(this.paramsWD);
  }

  formatNumber(n: string): string {
    return n.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.profileSubscription?.unsubscribe();
  }
}
